import type { ReactNode } from "react";
import { Loader2, Lock } from "lucide-react";
import { Input, Switch, Textarea } from "@bookdental/ui";
import type { AparenciaDoCatalogo } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { ColorPickerField } from "@/components/settings/ColorPickerField";
import { CampoDaCapa } from "./CampoDaCapa";
import type { EditorDeAparencia } from "./use-aparencia-do-catalogo";

const AZUL = "#2457FF";

const FUNDOS: { valor: AparenciaDoCatalogo["fundo"]; rotulo: string }[] = [
  { valor: "branco", rotulo: "Branco" },
  { valor: "claro", rotulo: "Cor clara" },
  { valor: "marcaDagua", rotulo: "Marca d'água" },
];

const MOSTRAR: { chave: keyof AparenciaDoCatalogo["mostrar"]; rotulo: string }[] = [
  { chave: "cnpj", rotulo: "CNPJ" },
  { chave: "endereco", rotulo: "Endereço" },
  { chave: "fotos", rotulo: "Fotos dos serviços" },
  { chave: "descricoes", rotulo: "Descrições dos serviços" },
];

const CONTATOS: { chave: keyof AparenciaDoCatalogo["contatos"]; rotulo: string; exemplo: string }[] = [
  { chave: "whatsapp", rotulo: "WhatsApp", exemplo: "(11) 99999-0000" },
  { chave: "instagram", rotulo: "Instagram", exemplo: "@laboratorio" },
  { chave: "site", rotulo: "Site", exemplo: "www.laboratorio.com.br" },
  { chave: "email", rotulo: "E-mail", exemplo: "contato@laboratorio.com.br" },
];

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t border-zinc-100 pt-3 first:border-0 first:pt-0 dark:border-border">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">{titulo}</p>
      {children}
    </section>
  );
}

/** Cor, fundo, capa, rodapé e o que mostrar no catálogo; a pré-visualização ao lado muda na hora. */
export function PainelDeAparencia({ ed, temLogo }: { ed: EditorDeAparencia; temLogo: boolean }) {
  if (ed.carregando) return <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-zinc-400" />;
  const a = ed.rascunho!;

  return (
    <aside className="space-y-4 rounded-xl border border-zinc-200 bg-white p-4 dark:border-border dark:bg-card">
      {ed.bloqueada && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Personalizar o catálogo é do plano pago. Sem ele, o catálogo sai no visual padrão.
        </p>
      )}
      <fieldset disabled={ed.bloqueada} className="space-y-4 disabled:opacity-60">
        <Secao titulo="Cor de destaque">
          <label className="flex items-center justify-between gap-2 text-[13px] text-zinc-700 dark:text-foreground">
            Usar a cor da marca
            <Switch checked={!!a.cor} onCheckedChange={(v) => ed.mudar("cor", v ? AZUL : null)} disabled={ed.bloqueada} />
          </label>
          {a.cor && <ColorPickerField value={a.cor} onChange={(c) => ed.mudar("cor", c)} defaultColor={AZUL} disabled={ed.bloqueada} />}
        </Secao>

        <Secao titulo="Fundo das páginas">
          <div className="flex gap-1.5">
            {FUNDOS.map((f) => (
              <button
                key={f.valor}
                type="button"
                onClick={() => ed.mudar("fundo", f.valor)}
                aria-pressed={a.fundo === f.valor}
                className={cn(
                  "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
                  a.fundo === f.valor ? "border-brand-600 bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
                )}
              >
                {f.rotulo}
              </button>
            ))}
          </div>
          {a.fundo === "marcaDagua" && !temLogo && <p className="text-[11px] text-zinc-500">Sem logo cadastrado, a marca d'água vira a cor clara.</p>}
        </Secao>

        <Secao titulo="Capa">
          <CampoDaCapa ed={ed} />
        </Secao>

        <Secao titulo="Rodapé e contatos">
          {CONTATOS.map((c) => (
            <Input key={c.chave} value={a.contatos[c.chave]} placeholder={`${c.rotulo}: ${c.exemplo}`} aria-label={c.rotulo} onChange={(e) => ed.mudar("contatos", { [c.chave]: e.target.value })} />
          ))}
          <Textarea value={a.rodapeTexto} maxLength={200} rows={2} placeholder="Texto livre do rodapé (horário, prazo de entrega...)" aria-label="Texto do rodapé" onChange={(e) => ed.mudar("rodapeTexto", e.target.value)} />
        </Secao>

        <Secao titulo="Mostrar no catálogo">
          {MOSTRAR.map((m) => (
            <label key={m.chave} className="flex items-center justify-between gap-2 text-[13px] text-zinc-700 dark:text-foreground">
              {m.rotulo}
              <Switch checked={a.mostrar[m.chave]} onCheckedChange={(v) => ed.mudar("mostrar", { [m.chave]: v })} disabled={ed.bloqueada} />
            </label>
          ))}
        </Secao>
      </fieldset>

      {!ed.bloqueada && (
        <div className="flex items-center gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={ed.salvar}
            disabled={!ed.sujo || ed.salvando}
            className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {ed.salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar aparência
          </button>
          <button type="button" onClick={ed.voltarAoPadrao} className="h-9 rounded-md px-2 text-xs font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-muted">
            Voltar ao padrão
          </button>
        </div>
      )}
      {ed.sujo && <p className="text-center text-[11px] text-amber-600">Alterações não salvas</p>}
    </aside>
  );
}
